import { HttpClient, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import {
    BABOOR_ENVIRONMENT,
    BaboorEnvironmentConfig,
} from '@baboor/baboor-core'
import { ApiResponse } from '@baboor/common-models'
import { setShopDisplayTimezone } from '@baboor/common-util'
import { Observable, map } from 'rxjs'

export interface ShopItem {
    id: string
    name: string
    email: string | null
    phone: string | null
    address: string | null
    city: string | null
    state: string | null
    country: string | null
    postCode: string | null
    verifiedOn: string | null
    createdAt: string | null
    updatedAt: string | null
    planName?: string | null
}

export interface GalleryImage {
    id: string
    url: string | null
    filename: string | null
}

export interface ShopLookbookStyle {
    id: string
    name: string
    description: string | null
    categoryId: string | null
    gender: string | null
    images: {
        id: string
        url: string
        thumbnailUrl: string | null
        mediumUrl: string | null
        isCover: boolean
        altText: string | null
    }[]
    services: { id: string; name: string }[]
}

export interface ShopBusinessHours {
    day: string
    isOpen: boolean
    openingTime: string | null
    closingTime: string | null
}

export interface ShopSettings {
    slotInterval: number
    /** IANA timezone the shop's wall-clock schedules are interpreted in. */
    timezone: string
}

export interface ShopProfile {
    shop: {
        id: string
        name: string
        email: string | null
        phone: string | null
        address: string | null
        city: string | null
        state: string | null
        country: string | null
        postCode: string | null
        logoUrl: string | null
        coverUrl?: string | null
        description: string | null
        slotInterval: number | null
        /** IANA zone the shop's wall-clock schedules are interpreted in. */
        timezone?: string | null
        latitude?: number | null
        longitude?: number | null
    }
    businessHours: ShopBusinessHours[]
    services: {
        id: string
        name: string
        description: string | null
        price: string
        duration: number
        category: string | null
        isActive: boolean
        promotion: {
            name: string
            discountType: string
            discountValue: string
            finalPrice: string
        } | null
    }[]
    team: {
        id: string
        userId: string
        role: string
        bio?: string | null
        isActive: boolean
        user?: {
            firstName: string
            lastName: string
            email: string | null
            phone: string | null
            profilePhoto: string | null
            bio?: string | null
        } | null
    }[]
    gallery: GalleryImage[]
    googleReviews?: {
        reviews: {
            id: string
            reviewerName: string
            reviewerPhotoUrl?: string | null
            starRating: number
            comment?: string | null
            reviewReply?: string | null
            reviewReplyTime?: string | null
            reviewUrl?: string | null
            reviewCreateTime: string
        }[]
        averageRating: number
        totalReviews: number
        showOnWebsite: boolean
        locationName: string | null
    } | null
}

export interface ShopReviewItem {
    id?: string
    organizationId?: string
    clientId?: string
    rating?: number
    comment?: string | null
    createdAt?: Date
    updatedAt?: Date
    clientFirstName?: string | null
    clientLastName?: string | null
    clientProfilePhoto?: string | null
}

export interface FavoritePromotion {
    id: string
    organizationId: string
    serviceId: string
    name: string
    discountType: string
    discountValue: string
    startDate: Date
    endDate: Date
    isActive: boolean
    createdAt: Date | null
    updatedAt: Date | null
    serviceName: string | null
    servicePrice: string | null
}

@Injectable({ providedIn: 'root' })
export class ShopsApiService {
    private apiUrl: string

    constructor(
        private http: HttpClient,
        @Inject(BABOOR_ENVIRONMENT) private env: BaboorEnvironmentConfig,
    ) {
        this.apiUrl = `${env.apiUrl}/v1/shops`
    }

    getAll(params?: {
        search?: string
        page?: number
        size?: number
    }): Observable<ApiResponse<ShopItem[]>> {
        let httpParams = new HttpParams()
        if (params?.search) httpParams = httpParams.set('search', params.search)
        if (params?.page) httpParams = httpParams.set('page', params.page)
        if (params?.size) httpParams = httpParams.set('size', params.size)
        return this.http.get<ApiResponse<ShopItem[]>>(this.apiUrl, {
            params: httpParams,
        })
    }

    getProfile(id: string): Observable<ApiResponse<ShopProfile>> {
        return this.http
            .get<ApiResponse<ShopProfile>>(`${this.apiUrl}/${id}/profile`)
            .pipe(
                map((response) => {
                    setShopDisplayTimezone(response.data?.shop?.timezone)
                    return response
                }),
            )
    }

    uploadGalleryImage(
        id: string,
        file: File,
    ): Observable<ApiResponse<GalleryImage>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<GalleryImage>>(
            `${this.apiUrl}/${id}/gallery`,
            formData,
        )
    }

    deleteGalleryImage(
        id: string,
        imageId: string,
    ): Observable<ApiResponse<null>> {
        return this.http.delete<ApiResponse<null>>(
            `${this.apiUrl}/${id}/gallery/${imageId}`,
        )
    }

    uploadCoverImage(
        id: string,
        file: File,
    ): Observable<ApiResponse<GalleryImage>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<GalleryImage>>(
            `${this.apiUrl}/${id}/cover`,
            formData,
        )
    }

    uploadLogoImage(
        id: string,
        file: File,
    ): Observable<ApiResponse<{ logoUrl: string }>> {
        const formData = new FormData()
        formData.append('file', file)
        return this.http.post<ApiResponse<{ logoUrl: string }>>(
            `${this.apiUrl}/${id}/logo`,
            formData,
        )
    }

    toggleFavorite(
        id: string,
    ): Observable<ApiResponse<{ isFavorited: boolean }>> {
        return this.http.post<ApiResponse<{ isFavorited: boolean }>>(
            `${this.apiUrl}/${id}/favorite`,
            {},
        )
    }

    getFavorites(params?: {
        search?: string
        page?: number
        size?: number
    }): Observable<ApiResponse<ShopItem[]>> {
        let httpParams = new HttpParams()
        if (params?.search) httpParams = httpParams.set('search', params.search)
        if (params?.page) httpParams = httpParams.set('page', params.page)
        if (params?.size) httpParams = httpParams.set('size', params.size)
        return this.http.get<ApiResponse<ShopItem[]>>(
            `${this.apiUrl}/favorites`,
            { params: httpParams },
        )
    }

    checkFavoriteStatus(
        id: string,
    ): Observable<ApiResponse<{ isFavorited: boolean }>> {
        return this.http.get<ApiResponse<{ isFavorited: boolean }>>(
            `${this.apiUrl}/${id}/favorite-status`,
        )
    }

    getLookbookStyles(
        id: string,
    ): Observable<ApiResponse<ShopLookbookStyle[]>> {
        return this.http.get<ApiResponse<ShopLookbookStyle[]>>(
            `${this.apiUrl}/${id}/lookbook-styles`,
        )
    }

    getShopReviews(
        shopId: string,
        params?: { page?: number; size?: number },
    ): Observable<
        ApiResponse<{
            data: ShopReviewItem[]
            pagination: { page: number; size: number; total: number }
        }>
    > {
        let httpParams = new HttpParams()
        if (params?.page) httpParams = httpParams.set('page', params.page)
        if (params?.size) httpParams = httpParams.set('size', params.size)
        return this.http.get<
            ApiResponse<{
                data: ShopReviewItem[]
                pagination: { page: number; size: number; total: number }
            }>
        >(`${this.apiUrl}/${shopId}/reviews`, { params: httpParams })
    }

    getFavoritePromotions(): Observable<ApiResponse<FavoritePromotion[]>> {
        return this.http.get<ApiResponse<FavoritePromotion[]>>(
            `${this.apiUrl}/favorites/promotions`,
        )
    }

    /** Persisted shop settings (`shop_details`). */
    getSettings(): Observable<ApiResponse<ShopSettings>> {
        return this.http
            .get<ApiResponse<ShopSettings>>(`${this.apiUrl}/settings`)
            .pipe(
                map((response) => {
                    setShopDisplayTimezone(response.data?.timezone)
                    return response
                }),
            )
    }

    updateSettings(settings: {
        slotInterval: number
        timezone?: string
    }): Observable<ApiResponse<ShopSettings>> {
        return this.http
            .put<ApiResponse<ShopSettings>>(`${this.apiUrl}/settings`, settings)
            .pipe(
                map((response) => {
                    setShopDisplayTimezone(response.data?.timezone)
                    return response
                }),
            )
    }
}
